import { ComponentType, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Badge, Button, Table } from 'react-bootstrap';
import { ArrowLeft, Save, Send } from 'lucide-react';
import { I18n } from '@/i18n/I18n';
import { RunReportVersionSummary } from '@/backend/access/runReport/types';
import { ReportEvaluation, ReportValues, RunReportContext, asRunReport } from '@/reports/types';
import { useGlobalStore } from '@/frontend/store/GlobalStore';
import { useRunReportStore } from '@/frontend/store/RunReportStore';
import { ReportAssetsProvider, ReportEditingProvider, ReportOutline, ReportOutlineProvider, outstanding, useNewOutlineStore, useOutline } from '@/frontend/reports/primitives';
import { useReportModule } from '@/frontend/reports/useReportModule';
import { ReportViewProps } from '@/frontend/reports/types';
import { FusaToastType } from '@/frontend/toasts/types';
import { FusawarePermission } from '@/frontend/fusawareUser';
import { ConfirmModal } from '../modals/ConfirmModal';
import { LoadingSpinner } from '../LoadingSpinner';
import { VerdictBadge, runStatusVariant } from './ReportBadges';
import { isEmbedApp } from '@/frontend/embed/embedApp';

/** The run a report is on, as the page heads it: title, status, a way back. */
export function RunReportHeading({ run, canEdit }: { run: { id: string; title: string; status: string; sourceTitle: string | null }; canEdit: boolean }) {
  return (
    <div className="d-flex align-items-center gap-3 flex-wrap">
      {canEdit && (
        <Link to={`/procedures/run/${run.id}`} className="btn btn-blue-light fusa-btn-small d-flex align-items-center gap-1">
          <ArrowLeft size={14} /> {I18n.get('backToRun')}
        </Link>
      )}
      <h4 className="mb-0">{run.title}</h4>
      <Badge bg={runStatusVariant(run.status)}>{run.status}</Badge>
      {run.sourceTitle && <span className="text-muted small">{run.sourceTitle}</span>}
    </div>
  );
}

/**
 * The test report on one procedure run: its inputs, the live document, and
 * the versions published from it. The org that executed the run edits and
 * publishes; anyone else who can see the run — the project's owner, the
 * workspace's executor — reads.
 */
export function RunReportPage() {
  const { runId = '' } = useParams();
  const addToast = useGlobalStore((s) => s.addToast);
  const canManageTemplates = !!useGlobalStore((s) => s.userClass)?.fusaHasPermission(FusawarePermission.MANAGE_PROCEDURE_TEMPLATES);
  const state = useRunReportStore((s) => s.stateByRun[runId]);
  const missing = useRunReportStore((s) => !!s.missingRuns[runId]);
  const fetchState = useRunReportStore((s) => s.fetchState);
  const saveValues = useRunReportStore((s) => s.saveValues);
  const remove = useRunReportStore((s) => s.remove);
  const create = useRunReportStore((s) => s.create);
  const publish = useRunReportStore((s) => s.publish);
  const fileUrls = useRunReportStore((s) => s.fileUrls);
  const outlineStore = useNewOutlineStore();
  const outline = useOutline(outlineStore);
  const [loading, setLoading] = useState(true);
  const [showRemove, setShowRemove] = useState(false);
  const [showPublish, setShowPublish] = useState(false);
  const [saving, setSaving] = useState(false);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState<ReportValues | null>(null);

  useEffect(() => {
    setLoading(true);
    void fetchState(runId).finally(() => setLoading(false));
  }, [runId, fetchState]);

  const report = state?.runReport ?? null;
  const canEdit = !!state?.canEdit;
  const { module, loading: moduleLoading, error: moduleError } = useReportModule(report ? { key: report.reportKey, bundleVersionId: state?.bundleVersionId ?? null } : null);
  const loadedDefinition = module?.definition;
  const definition = loadedDefinition ? asRunReport(loadedDefinition) : null;
  const components = module?.components;
  const context = state?.context ?? null;

  const stored = useMemo(() => (report && loadedDefinition ? loadedDefinition.normalizeValues(report.values) : null), [report, loadedDefinition]);
  useEffect(() => setDraft(null), [report?.id, report?.updatedAt]);
  const values = draft ?? stored;
  const dirty = draft !== null;

  const evaluated = useMemo(() => {
    if (!definition || !values || !context) return null;
    try {
      return { model: definition.evaluate(values, context), error: null };
    } catch (error) {
      return { model: null, error: error instanceof Error ? error.message : String(error) };
    }
  }, [definition, values, context]);
  const model = evaluated?.model ?? null;
  const resolveFiles = useCallback((ids: string[]) => fileUrls(runId, ids), [fileUrls, runId]);

  const handleSave = async (): Promise<boolean> => {
    if (!draft) return true;
    setSaving(true);
    try {
      const ok = await saveValues(runId, draft);
      if (ok) {
        setDraft(null);
        addToast({ type: FusaToastType.SUCCESS, header: I18n.get('reportSaved'), message: I18n.get('reportSavedMessage') });
      }
      return ok;
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    if (!(await handleSave())) return;
    await publish(runId);
  };

  const left = outstanding(outline);

  if (missing && !state) {
    return (
      <div className="p-4">
        <p className="text-muted">{I18n.get('modelNotFound', { model: I18n.get('procedureRun') })}</p>
      </div>
    );
  }
  if (loading || !state || moduleLoading) {
    return <div className="d-flex justify-content-center py-5" aria-label={I18n.get('loading')}><LoadingSpinner /></div>;
  }

  const shell = (body: ReactNode) => (
    <div className="overflow-auto" style={{ maxHeight: '100vh', height: '100vh' }}>
      <div className="p-4 d-flex flex-column gap-3">
        <RunReportHeading run={state.run} canEdit={canEdit} />
        {body}
      </div>
    </div>
  );

  if (!report) {
    // the report the procedure template's author put on it: the one report this run can get
    const template = state.reportTemplate;
    const handleCreate = async () => {
      setCreating(true);
      try {
        await create(runId);
      } finally {
        setCreating(false);
      }
    };
    return shell(
      <div className="list-card">
        <p className="list-card-title">{I18n.get('testReport')}</p>
        {!template ? (
          <p className="text-muted mb-2 px-2">{I18n.get('runHasNoCodedReportTemplate')}</p>
        ) : !canEdit ? (
          <p className="text-muted mb-2 px-2">{I18n.get('noRunReportReader')}</p>
        ) : (
          <div className="px-2 pb-2 d-flex flex-column gap-2">
            <p className="mb-0">{I18n.get('noRunReportYet', { title: template.title })}</p>
            <div className="d-flex align-items-center gap-3 flex-wrap">
              <Button className="fusa-btn-medium fusa-btn-primary" onClick={() => void handleCreate()} disabled={creating || !template.reportTitle}>
                {creating ? I18n.get('saving') : I18n.get('createTestReport')}
              </Button>
              <span className="small text-muted">{template.reportTitle ?? I18n.get('unknownReportKey')}</span>
            </div>
          </div>
        )}
      </div>,
    );
  }

  if (!definition || !components || !values || !model || !context) {
    const message = moduleError
      ? I18n.get('reportBundleLoadFailed', { message: moduleError })
      : evaluated?.error
        ? I18n.get('reportBundleLoadFailed', { message: evaluated.error })
        : I18n.get('unknownReportKey');
    return shell(
      <div className="list-card">
        <p className="list-card-title">{I18n.get('testReport')}</p>
        <p className="text-danger px-2 mb-2">{message}</p>
        {canEdit && <div className="px-2 pb-2"><Button className="btn-red-light fusa-btn-small" onClick={() => setShowRemove(true)}>{I18n.get('removeReport')}</Button></div>}
        {showRemove && <ConfirmModal title={I18n.get('removeReport')} confirmMessage={I18n.get('removeRunReportConfirm')} onConfirm={() => remove(runId)} setShowModal={setShowRemove} />}
      </div>,
    );
  }

  const Document = components.Document as unknown as ComponentType<ReportViewProps<ReportValues, ReportEvaluation, RunReportContext>>;
  const onChange = canEdit ? (next: ReportValues) => setDraft(next) : undefined;
  const meta = { reportVersion: definition.version };

  return shell(
    <>
      <div className="list-card">
        <div className="d-flex justify-content-between align-items-start gap-3 px-2">
          <div>
            <p className="list-card-title mb-1 px-0">{I18n.get('testReport')}</p>
            <h5 className="list-card-block-title">{definition.title}</h5>
            <div className="small text-muted d-flex flex-wrap gap-3 mt-1">
              <span>v{definition.version}</span>
              <span><VerdictBadge verdict={model.verdict} label={model.verdictLabel} /></span>
              {context.reportTemplate && (
                <span>
                  <span>{I18n.get('fromReportTemplate')}</span>{' '}
                  {canManageTemplates && isEmbedApp() ? <Link to={`/reports/edit/${context.reportTemplate.id}`}>{context.reportTemplate.title}</Link> : context.reportTemplate.title}
                </span>
              )}
              {report.updatedAt && <span>{I18n.get('lastSaved')} {new Date(report.updatedAt).toLocaleString()} · {report.updatedBy}</span>}
            </div>
            <p className="small text-muted mb-0 mt-2">{canEdit ? I18n.get('runReportReadsRun') : I18n.get('runReportReadOnly')}</p>
          </div>
          {canEdit && (
            <div className="d-flex gap-2 flex-shrink-0">
              <Button className="btn-red-light fusa-btn-small" onClick={() => setShowRemove(true)}>{I18n.get('removeReport')}</Button>
            </div>
          )}
        </div>
        {canEdit && (
          <div className="d-flex justify-content-between align-items-center gap-2 mt-3 px-2">
            <span className="small text-muted">{dirty ? I18n.get('unsavedChanges') : I18n.get('allChangesSaved')}</span>
            <div className="d-flex gap-2">
              <Button className="btn-blue-light fusa-btn-medium d-flex align-items-center gap-2" onClick={() => void handleSave()} disabled={!dirty || saving} aria-label={I18n.get('saveReport')}>
                <Save size={16} /> {saving ? I18n.get('saving') : I18n.get('saveReport')}
              </Button>
              <Button className="fusa-btn-medium fusa-btn-primary d-flex align-items-center gap-2" onClick={() => setShowPublish(true)} disabled={saving} aria-label={I18n.get('publishReport')}>
                <Send size={16} /> {I18n.get('publishReport')}
              </Button>
            </div>
          </div>
        )}
      </div>

      <div className="report-with-outline">
        <div className="report-with-outline-document">
          <ReportOutlineProvider store={outlineStore}>
            <ReportAssetsProvider resolve={resolveFiles}>
              <ReportEditingProvider readOnly={!canEdit}>
                <Document values={values} model={model} context={context} meta={meta} onChange={onChange} />
              </ReportEditingProvider>
            </ReportAssetsProvider>
          </ReportOutlineProvider>
        </div>
        {canEdit && <aside className="report-with-outline-rail"><ReportOutline store={outlineStore} /></aside>}
      </div>

      <RunReportVersionsCard runId={runId} versions={state.versions} />

      {showRemove && <ConfirmModal title={I18n.get('removeReport')} confirmMessage={I18n.get('removeRunReportConfirm')} onConfirm={() => remove(runId)} setShowModal={setShowRemove} />}
      {showPublish && (
        <ConfirmModal
          title={I18n.get('publishReport')}
          confirmMessage={left.length ? I18n.get('publishReportConfirmWithEmpty', { verdict: model.verdictLabel, count: String(left.length) }) : I18n.get('publishReportConfirm', { verdict: model.verdictLabel })}
          confirmText={I18n.get('publish')}
          onConfirm={handlePublish}
          setShowModal={setShowPublish}
        />
      )}
    </>,
  );
}

function RunReportVersionsCard({ runId, versions }: { runId: string; versions: RunReportVersionSummary[] }) {
  return (
    <div className="list-card">
      <p className="list-card-title">{I18n.get('publishedVersions')}</p>
      {versions.length === 0 ? (
        <p className="text-muted small mb-0 px-2">{I18n.get('noPublishedVersions')}</p>
      ) : (
        <Table size="sm" hover className="align-middle mb-0">
          <thead>
            <tr>
              <th>{I18n.get('version')}</th>
              <th>{I18n.get('date')}</th>
              <th>{I18n.get('status')}</th>
              <th>{I18n.get('publishedBy')}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {versions.map((v) => (
              <tr key={v.id}>
                <td>{v.number}</td>
                <td>{new Date(v.createdAt ?? '').toLocaleString()}</td>
                <td><VerdictBadge verdict={v.verdict} label={v.verdictLabel} /></td>
                <td>{v.publishedBy}</td>
                <td className="text-end">
                  <Link to={`/runs/${runId}/report/versions/${v.id}`} className="btn btn-blue-light fusa-btn-small">{I18n.get('viewVersion', { version: String(v.number) })}</Link>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </div>
  );
}
